// BottomNav — global nav. Two variants:
//
//   standard   — [Start • Live • Recap • You] on every screen except /night/*
//   in-stumble — [Map • Camera • End • You] on /night/$id routes, replacing
//                the in-stumble action row so the screen fits 390×666 with
//                Safari chrome shown (CLAUDE.md §5 rule 8 / iPhone X budget).
//
// Hidden entirely on /login and /onboarding.
//
// Standard variant ported from design-canon/project/src/components.jsx → BottomNav.
// In-stumble variant is project-specific (F-Wave PR α / F8).
//
// Camera + End slots dispatch global DOM custom events:
//   - 'stumble:open-camera'    → drink-logger opens SelfieSheet
//   - 'stumble:open-end-night' → drink-logger opens End Night confirm
// This keeps the nav stateless / props-free and lets the active night route
// own its own modal state.
//
// Canon shape (do not deviate):
//   - 4 items in a 4-column pill (border-radius: 999px)
//   - Border 2.5px var(--line); shadow 0 6px 0 var(--shadow-ink); bg var(--paper)
//   - Icon size 20, label below in 10px / 900 / 0.08em / uppercase
//   - Active: var(--ink) background, var(--paper) text — full pill swap
//   - No tap-scale animation (canon has none — see canon.css §.bottom-nav)
//
// Per CLAUDE.md §5 rule #5: position fixed + max(env(safe-area-inset-bottom),
// var(--vv-bottom-chrome)). The override CSS handles that; AppShell publishes
// --vv-bottom-chrome via useVisualViewport so the nav lifts above iOS Safari's
// URL bar and the soft keyboard.

import { Link, useRouterState } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { nightApi } from '../api'
import { Icon, type IconName } from './Icon'

/** Custom DOM events the in-stumble nav fires. Drink logger listens. */
export const OPEN_CAMERA_EVENT = 'stumble:open-camera'
export const OPEN_END_NIGHT_EVENT = 'stumble:open-end-night'

type LinkItem = {
  kind: 'link'
  to: string
  icon: IconName
  label: string
  match: (p: string) => boolean
}
type ActionItem = {
  kind: 'action'
  event: typeof OPEN_CAMERA_EVENT | typeof OPEN_END_NIGHT_EVENT
  icon: IconName
  label: string
  testid: string
  ariaLabel: string
}
type Item = LinkItem | ActionItem

const STANDARD_ITEMS: Item[] = [
  { kind: 'link', to: '/', icon: 'home', label: 'Start', match: (p) => p === '/' },
  {
    kind: 'link',
    to: '/stumbling',
    icon: 'map',
    label: 'Live',
    match: (p) => p.startsWith('/stumbling'),
  },
  {
    kind: 'link',
    to: '/history',
    icon: 'book',
    label: 'Recap',
    match: (p) => p.startsWith('/history'),
  },
  { kind: 'link', to: '/you', icon: 'you', label: 'You', match: (p) => p.startsWith('/you') },
]

// In-stumble: Map • Camera • End • You. Map navigates to /stumbling (live
// board); Camera + End dispatch events handled by the active night route;
// You navigates to /you.
const IN_STUMBLE_ITEMS: Item[] = [
  {
    kind: 'link',
    to: '/stumbling',
    icon: 'map',
    label: 'Map',
    match: (p) => p.startsWith('/stumbling'),
  },
  {
    kind: 'action',
    event: OPEN_CAMERA_EVENT,
    icon: 'camera',
    label: 'Camera',
    testid: 'nav-camera',
    ariaLabel: 'Take a selfie',
  },
  {
    kind: 'action',
    event: OPEN_END_NIGHT_EVENT,
    icon: 'flag',
    label: 'End',
    testid: 'nav-end',
    ariaLabel: 'End your stumble',
  },
  { kind: 'link', to: '/you', icon: 'you', label: 'You', match: (p) => p.startsWith('/you') },
]

function dispatchNav(event: string) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(event))
}

export function BottomNav() {
  const path = useRouterState({ select: (s) => s.location.pathname })

  // G0.3: status-aware in-stumble variant. Only flip to [Map • Camera • End •
  // You] when the route's night is actually *active*. On ENDED night recaps
  // (e.g. /night/$id/recap) the standard variant must show — the in-stumble
  // Camera/End actions are nonsensical there. Match /night/$id and /night/$id/*.
  const nightIdMatch = path.match(/^\/night\/([^/]+)/)
  const nightId = nightIdMatch?.[1] ?? null
  const [nightStatus, setNightStatus] = useState<'loading' | 'active' | 'ended' | null>(null)

  useEffect(() => {
    if (!nightId) {
      setNightStatus(null)
      return
    }
    setNightStatus('loading')
    const ac = new AbortController()
    nightApi
      .get(nightId, { signal: ac.signal })
      .then((res) => {
        const status = res.night.night.status
        setNightStatus(status === 'active' ? 'active' : 'ended')
      })
      .catch(() => {
        // Network error / aborted / 404 — fall back to standard variant.
        setNightStatus(null)
      })
    return () => ac.abort()
  }, [nightId])

  // Hide entirely on login + onboarding — those screens own their own CTA.
  if (path === '/login' || path.startsWith('/onboarding')) return null

  // Default during load = 'standard' (avoids flickering into the [MAP CAMERA
  // END YOU] variant on recap screens before the status fetch resolves).
  const variant: 'standard' | 'in-stumble' =
    nightId && nightStatus === 'active' ? 'in-stumble' : 'standard'
  const items = variant === 'in-stumble' ? IN_STUMBLE_ITEMS : STANDARD_ITEMS

  return (
    <nav
      className="bottom-nav"
      aria-label="Primary"
      data-testid="bottom-nav"
      data-variant={variant}
    >
      {items.map((it) => {
        if (it.kind === 'link') {
          const active = it.match(path)
          return (
            <Link
              key={`link-${it.to}`}
              to={it.to}
              className={active ? 'active' : ''}
              aria-current={active ? 'page' : undefined}
            >
              <Icon name={it.icon} size={20} />
              <span style={{ marginTop: 4 }}>{it.label}</span>
            </Link>
          )
        }
        return (
          <button
            type="button"
            key={`action-${it.event}`}
            onClick={() => dispatchNav(it.event)}
            aria-label={it.ariaLabel}
            data-testid={it.testid}
          >
            <Icon name={it.icon} size={20} />
            <span style={{ marginTop: 4 }}>{it.label}</span>
          </button>
        )
      })}
    </nav>
  )
}
